import React, { useEffect, useState, useRef } from "react";
import { Spin, Alert, Empty } from "antd";
import Changelogs from "../../models/changelogs/changelog";
import BaseService from "../../service/base.service";
import { useIsMobile } from "../../hooks/useIsMobile";

const typeLabels: Record<string, string> = {
  feature: "✨ Feature",
  improvement: "🛠️ Improvement",
  bugfix: "🐛 Bug Fix",
  internal: "🔧 Internal",
  other: "📦 Other",
};

const ChangelogPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [changelogs, setChangelogs] = useState<Changelogs[]>([]);
  const [visibleVersions, setVisibleVersions] = useState<boolean[]>([]);
  const [visibleChanges, setVisibleChanges] = useState<boolean[][]>([]);
  const [loading, setLoading] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchChangelogs();
  }, []);

  const fetchChangelogs = async () => {
    setLoading(true);
    try {
      const rp = await BaseService.getAll<Changelogs>(`/changelogs`);
      if (rp.Status) {
        const data = rp.Data || [];
        setChangelogs(
          data.map((a: any) => new Changelogs(a.id, a.version, a.date, a.changes))
        );
        setVisibleVersions(Array(data.length).fill(false));
        setVisibleChanges(data.map((v: Changelogs) => Array(v.changes.length).fill(false)));
        setLoading(false);

        const versionDelay = isMobile ? 400 : 800;
        const changeDelay = isMobile ? 120 : 250;

        data.forEach((version: Changelogs, i: number) => {
          setTimeout(() => {
            setVisibleVersions((prev) => {
              const copy = [...prev];
              copy[i] = true;
              return copy;
            });
          }, i * versionDelay);

          version.changes.forEach((_, j) => {
            const delay = i * versionDelay + j * changeDelay;
            setTimeout(() => {
              setVisibleChanges((prev) => {
                const copy = prev.map((arr) => [...arr]);
                copy[i][j] = true;
                return copy;
              });
            }, delay);
          });
        });
      } else {
        setLoading(false);
      }
    } catch (err) {
      console.error("Error fetching changelogs:", err);
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        padding: isMobile ? "8px 0 16px" : "16px 16px 24px",
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: isMobile ? 12 : 24, flexShrink: 0 }}>
        <Alert
          message={
            isMobile
              ? "Latest features, improvements, and fixes."
              : "Stay up to date with the latest features, improvements, and fixes."
          }
          type="info"
          showIcon={!isMobile}
          style={{
            background: "#f0f7ff",
            borderColor: "#d0e4ff",
            color: "#2c5282",
            borderRadius: 8,
            padding: isMobile ? "8px 12px" : "10px 14px",
            fontSize: isMobile ? 13 : 14,
          }}
        />
      </div>

      <div
        style={{
          background: "#fff",
          border: "1px solid #f0f0f0",
          borderRadius: isMobile ? 10 : 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: isMobile ? "16px 16px 12px" : "24px 32px 16px",
            borderBottom: "1px solid #f0f0f0",
            flexShrink: 0,
          }}
        >
          <h1
            style={{
              fontSize: isMobile ? "1.25rem" : "1.75rem",
              fontWeight: 600,
              color: "#1f1f1f",
              margin: 0,
            }}
          >
            What's New
          </h1>
        </div>

        <div
          ref={contentRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            padding: isMobile ? "16px" : "24px 32px",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {loading ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                gap: 12,
                minHeight: isMobile ? 160 : 240,
              }}
            >
              <Spin size="large" />
              <span
                style={{
                  color: "#6b7280",
                  fontSize: isMobile ? 13 : 14,
                  whiteSpace: "nowrap",
                }}
              >
                Loading What's New...
              </span>
            </div>
          ) : changelogs.length === 0 ? (
            <Empty description="No updates yet" style={{ marginTop: isMobile ? 24 : 48 }} />
          ) : (
            changelogs.map((changelog, i) => (
              <div
                key={changelog.version}
                style={{
                  marginBottom: isMobile ? 20 : 32,
                  borderBottom: "1px solid #eaeaea",
                  paddingBottom: isMobile ? 12 : 16,
                  opacity: visibleVersions[i] ? 1 : 0,
                  transform: visibleVersions[i] ? "translateY(0)" : "translateY(20px)",
                  transition: "opacity 0.6s ease, transform 0.6s ease",
                }}
              >
                <h2
                  style={{
                    fontSize: isMobile ? "1.05rem" : "1.25rem",
                    marginBottom: 8,
                    color: "#1677ff",
                    display: "flex",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "baseline",
                    gap: isMobile ? 2 : 8,
                  }}
                >
                  <span>Version v{changelog.version}</span>
                  <small style={{ color: "#888", fontWeight: 400 }}>({changelog.date})</small>
                </h2>

                <ul
                  style={{
                    listStyle: "none",
                    paddingLeft: isMobile ? 0 : "1rem",
                    marginTop: 12,
                  }}
                >
                  {changelog.changes.map((change, j) => (
                    <li
                      key={j}
                      style={{
                        fontSize: isMobile ? 14 : 16,
                        marginBottom: isMobile ? 10 : 6,
                        color: "#333",
                        lineHeight: 1.45,
                        wordBreak: "break-word",
                        opacity: visibleChanges[i]?.[j] ? 1 : 0,
                        transform: visibleChanges[i]?.[j] ? "translateY(0)" : "translateY(10px)",
                        transition: "opacity 0.5s ease, transform 0.5s ease",
                      }}
                    >
                      <strong style={{ marginRight: 6 }}>{typeLabels[change.type] ?? change.type}:</strong>
                      {change.description}
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ChangelogPage;
